import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, 
  Plus, 
  Search,
  Download,
  Eye,
  Trash2,
  Upload,
  File,
  Image,
  FileSpreadsheet
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// File type icons
const FILE_ICONS = {
  pdf: FileText,
  dwg: FileSpreadsheet,
  dxf: FileSpreadsheet,
  png: Image,
  jpg: Image,
  jpeg: Image,
  default: File,
};

function DrawingsPanel({ isOpen, onClose, title = "Reference" }) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch drawings for current tour
  const fetchDrawings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/drawings/tour/${currentTour.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDrawings(data);
      }
    } catch (err) {
      console.error('Failed to fetch drawings:', err);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchDrawings();
    }
  }, [isOpen, fetchDrawings]);

  // Filter drawings by search
  const filteredDrawings = drawings.filter(d => 
    d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.drawing_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get icon for file type
  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return FILE_ICONS[ext] || FILE_ICONS.default;
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            {title}
          </SheetTitle>
          <SheetDescription>
            View and manage reference documents and drawings
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)] mt-4">
          {/* Search and Add */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search drawings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {canEdit && (
              <Button size="sm" className="gap-1" disabled={!currentTour?.id}>
                <Plus className="h-3 w-3" />
                Add
              </Button>
            )}
          </div>

          {/* Viewer notice */}
          {isViewer && (
            <div className="text-xs text-muted-foreground bg-secondary/50 p-2 rounded mb-4">
              View-only access. Contact an admin to manage drawings.
            </div>
          )}

          {/* Drawings list */}
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredDrawings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No drawings found</p>
                <p className="text-xs mt-1">Upload drawings to attach them to this tour</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredDrawings.map(drawing => {
                  const FileIcon = getFileIcon(drawing.filename);
                  return (
                    <div 
                      key={drawing.id}
                      className="p-3 rounded-lg border hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded bg-primary/10">
                          <FileIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{drawing.name}</p>
                          {drawing.drawing_number && (
                            <p className="text-xs text-muted-foreground">
                              #{drawing.drawing_number}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-[10px]">
                              {drawing.filename?.split('.').pop()?.toUpperCase()}
                            </Badge>
                            {drawing.revision && (
                              <Badge variant="outline" className="text-[10px]">
                                Rev {drawing.revision}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon-sm" title="View">
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button variant="ghost" size="icon-sm" title="Download">
                            <Download className="h-3 w-3" />
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon-sm" title="Delete">
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default DrawingsPanel;
